package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LArrowRightIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private val HeroFigureElement = ElementBuilder.createBuilder<HTMLElement>("figure")
private val HeroCaptionElement = ElementBuilder.createBuilder<HTMLElement>("figcaption")

/** Explain the product first; the image is an actual editor capture using example data. */
@Composable
fun HeroPitch() {
    Div(HeroPitchStyle.toModifier().toAttrs()) {
        Div(HeroLeadStyle.toModifier().toAttrs()) {
            H1(HeroHeadlineStyle.toModifier().toAttrs()) {
                Text(t("marketing.hero_pitch.shared_workspace_headline", "App translations, in one shared workspace."))
            }
            P(HeroLedeStyle.toModifier().toAttrs()) {
                Text(t("marketing.hero_pitch.draft_review_return",
                    "Bring your app’s strings. Draft with people or agents, review each language, and return translations to your repository."))
            }
            Div(HeroActionsStyle.toModifier().toAttrs()) {
                PublicActionLink("/signup", t("marketing.hero_pitch.start_project", "Start your project"))
                Link(
                    path = "#workflow",
                    modifier = HeroDocsLinkStyle.toModifier(),
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                ) {
                    Text(t("marketing.hero_pitch.see_workflow", "See how it works"))
                    LArrowRightIcon(HeroDocsArrowStyle.toModifier())
                }
            }
        }
        TagElement(elementBuilder = HeroFigureElement, applyAttrs = HeroProductFigureStyle.toModifier().toAttrs()) {
            Img(
                src = "/marketing/editor-review.jpg",
                alt = t("marketing.hero_pitch.editor_image_description",
                    "Lingvo translation editor with English source text, German plural drafts and separate approval controls."),
                attrs = HeroProductImageStyle.toModifier().toAttrs {
                    attr("width", "1280")
                    attr("height", "720")
                    attr("decoding", "async")
                },
            )
            TagElement(elementBuilder = HeroCaptionElement, applyAttrs = HeroProductCaptionStyle.toModifier().toAttrs()) {
                Text(t("marketing.hero_pitch.editor_example_caption", "Example: reviewing German plural forms before approval."))
                Link(path = "/marketing/editor-review.jpg", modifier = HeroDocsLinkStyle.toModifier()) {
                    Text(t("marketing.hero_pitch.view_full_editor", "View the full editor example"))
                }
            }
        }
    }
}

val HeroPitchStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(Space.XXXL).styleModifier {
            property("display", "grid")
            property("grid-template-columns", "minmax(0, 1fr)")
            property("align-items", "center")
        }
    }
    Breakpoint.LG {
        Modifier.gap(Space.Huge).styleModifier {
            property("grid-template-columns", "minmax(0, 0.85fr) minmax(0, 1.15fr)")
        }
    }
}

val HeroLeadStyle = CssStyle.base {
    Modifier.fillMaxWidth().gap(Space.XL).styleModifier {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "flex-start")
    }
}

val HeroHeadlineStyle = CssStyle {
    base {
        Modifier.displayFont().fontSize(TypeScale.Display).fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight).letterSpacing((-1.5).px)
            .margin(0.px).maxWidth(560.px).color(colorMode.toSitePalette().textPrimary)
    }
    Breakpoint.ZERO { Modifier.fontSize(TypeScale.Heading) }
    Breakpoint.LG { Modifier.fontSize(TypeScale.Hero) }
}

val HeroLedeStyle = CssStyle.base {
    Modifier.fontSize(TypeScale.Body).lineHeight(TypeScale.BodyLineHeight)
        .margin(0.px).maxWidth(480.px).color(colorMode.toSitePalette().textSecondary)
}

val HeroActionsStyle = CssStyle.base {
    Modifier.gap(Space.LG).styleModifier {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
    }
}

val HeroDocsLinkStyle = CssStyle {
    base {
        Modifier.fontSize(TypeScale.BodySmall).fontWeight(600).gap(Space.XS)
            .borderRadius(Radius.Control).color(colorMode.toSitePalette().textSecondary)
            .textDecorationLine(TextDecorationLine.None)
            .outline(0.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
            }.transitionColors()
    }
    hover { Modifier.color(colorMode.toSitePalette().textPrimary).textDecorationLine(TextDecorationLine.Underline) }
    focusVisible {
        Modifier.outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val HeroDocsArrowStyle = CssStyle.base { Modifier.iconSize(IconSize.MD) }

val HeroProductFigureStyle = CssStyle.base {
    Modifier.fillMaxWidth().margin(0.px).backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .borderRadius(Radius.Modal).styleModifier { property("overflow", "hidden") }
}

val HeroProductImageStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().styleModifier {
            property("display", "block")
            property("height", "auto")
            property("object-fit", "contain")
            property("object-position", "center")
        }
    }
    Breakpoint.LG {
        Modifier.styleModifier {
            property("height", "460px")
            property("object-fit", "cover")
            property("object-position", "right center")
        }
    }
}

val HeroProductCaptionStyle = CssStyle.base {
    Modifier.padding(Space.LG).fontSize(TypeScale.Small).lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textSecondary).gap(Space.SM).styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
        }
}
